const STORAGE_KEY = "accessoriesCheckoutMockV1";

const toNumber = (value, fallback = 0) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const toPositiveInt = (value, min = 1) => {
    const numeric = Math.floor(toNumber(value, min));
    if (!Number.isFinite(numeric)) return min;
    return Math.max(min, numeric);
};

const hasPositivePrice = (value) => toNumber(value, 0) > 0;

const readStore = () => {
    if (typeof window === "undefined" || !window.localStorage) {
        return {counter: 0, paymentCounter: 0, orders: {}, payments: {}, idempotency: {}};
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return {counter: 0, paymentCounter: 0, orders: {}, payments: {}, idempotency: {}};
        const parsed = JSON.parse(raw);
        return {
            counter: toPositiveInt(parsed?.counter ?? 0, 0),
            paymentCounter: toPositiveInt(parsed?.paymentCounter ?? 0, 0),
            orders: parsed?.orders && typeof parsed.orders === "object" ? parsed.orders : {},
            payments: parsed?.payments && typeof parsed.payments === "object" ? parsed.payments : {},
            idempotency: parsed?.idempotency && typeof parsed.idempotency === "object" ? parsed.idempotency : {},
        };
    } catch (error) {
        return {counter: 0, paymentCounter: 0, orders: {}, payments: {}, idempotency: {}};
    }
};

const writeStore = (store) => {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
};

const pad = (value, length = 6) => String(value).padStart(length, "0");

const toCatalogById = (catalog = {}) => {
    if (Array.isArray(catalog)) {
        return catalog.reduce((carry, item) => {
            if (!item?.id) return carry;
            carry[String(item.id)] = item;
            return carry;
        }, {});
    }
    if (catalog && typeof catalog === "object") return catalog;
    return {};
};

const resolveCheckoutType = ({purchaseLines = [], rentLines = []}) => {
    if (purchaseLines.length > 0 && rentLines.length > 0) return "mixed";
    if (rentLines.length > 0) return "rent_only";
    return "purchase_only";
};

const normalizeLine = (line = {}) => ({
    type: line.type,
    accessory_id: toNumber(line.accessory_id),
    name: line.name || "",
    quantity: toPositiveInt(line.quantity, 1),
    days: line.days === null || line.days === undefined ? null : toPositiveInt(line.days, 1),
    unit_price_including_vat: toNumber(line.unit_price_including_vat),
    deposit_per_unit_including_vat: toNumber(line.deposit_per_unit_including_vat),
    line_total_including_vat: toNumber(line.line_total_including_vat),
});

const computePreflight = ({payload = {}, catalog = {}, authToken = null}) => {
    const catalogById = toCatalogById(catalog);
    const purchaseInput = Array.isArray(payload?.accessories_purchase) ? payload.accessories_purchase : [];
    const rentInput = Array.isArray(payload?.accessories_rent) ? payload.accessories_rent : [];

    const errors = [];
    const lines = [];

    let purchaseTotal = 0;
    let rentTotal = 0;
    let depositTotal = 0;

    purchaseInput.forEach((line, index) => {
        const accessoryId = toNumber(line?.accessoryId);
        const quantity = toPositiveInt(line?.quantity, 1);
        const item = catalogById[String(accessoryId)];

        if (!item) {
            errors.push(`accessories_purchase[${index}].accessoryId_not_found`);
            return;
        }

        const unitPrice = toNumber(item?.prices?.purchase_including_vat);
        if (!hasPositivePrice(unitPrice)) {
            errors.push(`accessories_purchase[${index}].purchase_not_available`);
            return;
        }

        const lineTotal = quantity * unitPrice;
        purchaseTotal += lineTotal;
        lines.push(normalizeLine({
            type: "purchase",
            accessory_id: accessoryId,
            name: item?.name || `Accessory ${accessoryId}`,
            quantity,
            days: null,
            unit_price_including_vat: unitPrice,
            deposit_per_unit_including_vat: 0,
            line_total_including_vat: lineTotal,
        }));
    });

    rentInput.forEach((line, index) => {
        const accessoryId = toNumber(line?.accessoryId);
        const quantity = toPositiveInt(line?.quantity, 1);
        const days = toPositiveInt(line?.days, 1);
        const item = catalogById[String(accessoryId)];

        if (!item) {
            errors.push(`accessories_rent[${index}].accessoryId_not_found`);
            return;
        }

        const rentalUnitPrice = toNumber(item?.prices?.rental_including_vat);
        if (!hasPositivePrice(rentalUnitPrice)) {
            errors.push(`accessories_rent[${index}].rent_not_available`);
            return;
        }

        const depositPerUnit = toNumber(item?.prices?.deposit_including_vat, 0);
        const rentalLineTotal = quantity * days * rentalUnitPrice;
        const depositLineTotal = hasPositivePrice(depositPerUnit) ? quantity * depositPerUnit : 0;

        rentTotal += rentalLineTotal;
        depositTotal += depositLineTotal;

        lines.push(normalizeLine({
            type: "rent",
            accessory_id: accessoryId,
            name: item?.name || `Accessory ${accessoryId}`,
            quantity,
            days,
            unit_price_including_vat: rentalUnitPrice,
            deposit_per_unit_including_vat: depositPerUnit,
            line_total_including_vat: rentalLineTotal + depositLineTotal,
        }));
    });

    if (purchaseInput.length === 0 && rentInput.length === 0) {
        errors.push("cart_empty");
    }

    const checkoutType = resolveCheckoutType({
        purchaseLines: purchaseInput,
        rentLines: rentInput,
    });
    const requireRegistration = checkoutType !== "purchase_only";
    const canCheckout = errors.length === 0;

    return {
        checkout_type: checkoutType,
        require_registration: requireRegistration,
        can_checkout: canCheckout,
        errors,
        totals: {
            purchase_total_including_vat: purchaseTotal,
            rent_total_including_vat: rentTotal,
            deposit_total_including_vat: depositTotal,
            grand_total_including_vat: purchaseTotal + rentTotal + depositTotal,
        },
        lines,
        split_required: false,
        split_reason: null,
        _meta: {
            hasAuthToken: Boolean(authToken),
        }
    };
};

const buildMockOrder = ({
    orderId,
    payload,
    preflight,
    contact,
    customerId,
    hasAuthToken,
}) => {
    const requiresRegistration = preflight?.require_registration === true;
    const hasCustomer = customerId !== null && customerId !== undefined && String(customerId).trim() !== "";
    const hasContact =
        Boolean(contact?.name?.trim()) &&
        Boolean(contact?.email?.trim()) &&
        Boolean(contact?.phone?.trim());

    if (preflight?.can_checkout !== true) {
        return {
            order_id: orderId,
            status: "draft",
            payment_status: "pending",
            checkout_type: preflight?.checkout_type || "purchase_only",
            totals: preflight?.totals || {},
            lines: preflight?.lines || [],
            errors: preflight?.errors || ["checkout_not_possible"],
            next_action: "collect_contact",
        };
    }

    if (requiresRegistration && !hasCustomer && !hasAuthToken) {
        return {
            order_id: orderId,
            status: "awaiting_registration",
            payment_status: "pending",
            checkout_type: preflight.checkout_type,
            totals: preflight.totals,
            lines: preflight.lines,
            errors: ["registration_required"],
            next_action: "register",
            payload_snapshot: payload,
            contact,
        };
    }

    if (preflight.checkout_type === "purchase_only" && !hasContact) {
        return {
            order_id: orderId,
            status: "draft",
            payment_status: "pending",
            checkout_type: preflight.checkout_type,
            totals: preflight.totals,
            lines: preflight.lines,
            errors: ["contact_required"],
            next_action: "collect_contact",
            payload_snapshot: payload,
            contact,
        };
    }

    return {
        order_id: orderId,
        status: "awaiting_payment",
        payment_status: "pending",
        checkout_type: preflight.checkout_type,
        totals: preflight.totals,
        lines: preflight.lines,
        errors: [],
        next_action: "pay",
        payload_snapshot: payload,
        contact: contact || null,
        customer_id: hasCustomer ? customerId : null,
    };
};

const getIdempotentResponse = ({store, key}) => {
    if (!key) return null;
    const cached = store.idempotency?.[key];
    return cached || null;
};

const rememberIdempotentResponse = ({store, key, payload}) => {
    if (!key) return;
    if (!store.idempotency || typeof store.idempotency !== "object") {
        store.idempotency = {};
    }
    store.idempotency[key] = payload;
};

const preflightAccessoriesCheckoutMock = ({payload = {}, catalog = {}, authToken = null}) => {
    return computePreflight({payload, catalog, authToken});
};

const createAccessoriesDraftOrderMock = ({
    payload = {},
    catalog = {},
    contact = null,
    customerId = null,
    authToken = null,
    idempotencyKey = "",
}) => {
    const store = readStore();
    const idemCache = getIdempotentResponse({store, key: idempotencyKey});
    if (idemCache) return idemCache;

    const preflight = computePreflight({payload, catalog, authToken});
    store.counter += 1;
    const orderId = `ACC-${new Date().getFullYear()}-${pad(store.counter)}`;

    const order = buildMockOrder({
        orderId,
        payload,
        preflight,
        contact,
        customerId,
        hasAuthToken: Boolean(authToken),
    });

    const createdOrder = {
        ...order,
        created_at: new Date().toISOString(),
    };

    store.orders[orderId] = createdOrder;
    writeStore(store);

    const response = {
        order_id: createdOrder.order_id,
        status: createdOrder.status,
        checkout_type: createdOrder.checkout_type,
        totals: createdOrder.totals,
        next_action: createdOrder.next_action,
        errors: createdOrder.errors || [],
    };

    rememberIdempotentResponse({store, key: idempotencyKey, payload: response});
    writeStore(store);

    return response;
};

const createAccessoriesPaymentMock = ({
    orderId,
    returnUrl = "",
    cancelUrl = "",
    idempotencyKey = "",
}) => {
    const store = readStore();
    const idemCache = getIdempotentResponse({store, key: idempotencyKey});
    if (idemCache) return idemCache;

    const order = store.orders[String(orderId)];
    if (!order) {
        throw new Error("order_not_found");
    }

    store.paymentCounter += 1;
    const paymentId = `ACCPAY-${new Date().getFullYear()}-${pad(store.paymentCounter)}`;

    const paidUrl = returnUrl
        ? `${returnUrl}${returnUrl.includes("?") ? "&" : "?"}payment_status=paid&source=mock`
        : "";

    const payment = {
        payment_id: paymentId,
        payment_url: paidUrl || cancelUrl || "",
        status: "created",
    };

    store.payments[paymentId] = {
        payment_id: paymentId,
        order_id: order.order_id,
        status: "pending",
        created_at: new Date().toISOString(),
    };

    store.orders[String(orderId)] = {
        ...order,
        status: "awaiting_payment",
        payment_status: "pending",
        last_payment_id: paymentId,
    };

    rememberIdempotentResponse({store, key: idempotencyKey, payload: payment});
    writeStore(store);

    return payment;
};

const setAccessoriesMockOrderStatus = ({orderId, paymentStatus = ""}) => {
    const normalized = String(paymentStatus || "").toLowerCase();
    if (!normalized) return null;

    const store = readStore();
    const order = store.orders[String(orderId)];
    if (!order) return null;

    let nextOrderStatus = order.status;
    let nextPaymentStatus = order.payment_status || "pending";

    if (normalized === "paid" || normalized === "accepted" || normalized === "success") {
        nextOrderStatus = "paid";
        nextPaymentStatus = "paid";
    } else if (normalized === "cancelled" || normalized === "canceled") {
        nextOrderStatus = "cancelled";
        nextPaymentStatus = "cancelled";
    } else if (normalized === "failed" || normalized === "rejected" || normalized === "error") {
        nextOrderStatus = "payment_failed";
        nextPaymentStatus = "failed";
    }

    store.orders[String(orderId)] = {
        ...order,
        status: nextOrderStatus,
        payment_status: nextPaymentStatus,
    };
    writeStore(store);

    return store.orders[String(orderId)];
};

const getAccessoriesOrderMock = ({orderId}) => {
    const store = readStore();
    const order = store.orders[String(orderId)];
    if (!order) {
        throw new Error("order_not_found");
    }

    return {
        order_id: order.order_id,
        status: order.status,
        payment_status: order.payment_status || "pending",
        checkout_type: order.checkout_type,
        totals: order.totals,
        lines: order.lines || [],
    };
};

export {
    preflightAccessoriesCheckoutMock,
    createAccessoriesDraftOrderMock,
    createAccessoriesPaymentMock,
    getAccessoriesOrderMock,
    setAccessoriesMockOrderStatus,
};
